/**
 * Tokens visuais do gerador de gráficos.
 *
 * Os valores vieram de amostragem das 18 imagens de referência em
 * src/temp_charts_generator: fundo preto puro ocupando 50-68% da área e
 * séries em rampas de gradiente da paleta padrão do Office (#0070C0,
 * #F0F000, #A0E050) — sinal de que hoje esses gráficos são montados à mão
 * no Excel/PowerPoint. Reproduzir esses tons é o que faz a imagem gerada
 * passar por "gráfico da casa".
 */

// Saída em 2x do padrão histórico (302x183, que é o tamanho de todas as
// imagens gf01 já publicadas). Gerar em 604x366 deixa nítido em tela
// retina; a inserção na matéria continua ocupando 302 de largura.
export const LARGURA_SAIDA = 604;
export const ALTURA_SAIDA = 366;
export const ESCALA = 2; // 604/302 — todo tamanho de fonte é multiplicado por isto

// Um SVG rasterizado através de <img> não enxerga @font-face nem as fontes
// do documento: só resolve o que o sistema tem instalado. Por isso a fonte
// é fixa e web-safe — mudar isso quebra a exportação silenciosamente
// (o preview continua certo e a imagem sai com fallback).
export const FAMILIA_FONTE = "Arial, Helvetica, sans-serif";

export const FUNDO_PADRAO = "#000000";
export const TEXTO_PADRAO = "#FFFFFF";

/**
 * Rampas por série. `cor` é o tom claro (centro do cilindro / topo da
 * coluna chata) e `corFim` o tom escuro das bordas.
 *
 * Amarelo e Verde são os valores medidos pixel a pixel na referência
 * gf01rc-upload-1788373095274-19.jpg — não são aproximações.
 */
export const PALETA_SERIES = [
  { nome: "Amarelo", cor: "#FFFF0B", corFim: "#7D8015" },
  { nome: "Verde", cor: "#3DAA73", corFim: "#216443" },
  { nome: "Azul", cor: "#0070C0", corFim: "#003060" },
  { nome: "Vermelho", cor: "#FF3B30", corFim: "#7A0000" },
  { nome: "Laranja", cor: "#FF8C00", corFim: "#7A3E00" },
  { nome: "Ciano", cor: "#00CED1", corFim: "#005A5C" },
  { nome: "Roxo", cor: "#9370DB", corFim: "#3B2A6B" },
  { nome: "Cinza", cor: "#D0D0D0", corFim: "#5A5A5A" },
];

/**
 * Fundo da área de plotagem das referências: um degradê azul-ardósia que
 * vem da "parede" do gráfico 3D do Excel. Medido na mesma imagem.
 */
export const PLOT_FUNDO_PADRAO = {
  linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
  stops: [
    [0, "#0d1116"],
    [0.55, "#2c3949"],
    [1, "#6686ad"],
  ],
};

/** Cores avulsas usadas em títulos/subtítulos nas referências. */
export const PALETA_TEXTO = [
  { nome: "Branco", cor: "#FFFFFF" },
  { nome: "Amarelo", cor: "#F0F000" },
  { nome: "Verde", cor: "#7DD957" },
  { nome: "Azul claro", cor: "#4EA6FF" },
  { nome: "Laranja", cor: "#FF8C00" },
  { nome: "Vermelho", cor: "#FF4D4F" },
];

/**
 * Tamanhos em px já na escala final (604x366), calibrados contra as
 * imagens de referência — que foram desenhadas em 302x183, então cada
 * valor aqui é o dobro do que se vê nelas.
 */
export const FONTES = {
  titulo: 12 * ESCALA,
  subtitulo: 10 * ESCALA,
  fonteCredito: 6.5 * ESCALA,
  categoria: 10 * ESCALA,
  rotulo: 9 * ESCALA,
  legenda: 8 * ESCALA,
};

export const ROTULO_PADRAO = {
  corTexto: "#FFFFFF",
  corFundo: "#1A1A1A",
  corBorda: "#FFFFFF",
};

/**
 * Gradiente vertical no formato que o Highcharts entende.
 * Usado como `color` de série ou de ponto — sai inline no SVG, então
 * sobrevive à exportação (diferente de qualquer referência externa, que
 * contaminaria o canvas e faria o toBlob falhar).
 */
export const gradienteVertical = (corInicio, corFim) => ({
  linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
  stops: [
    [0, corInicio],
    [1, corFim || corInicio],
  ],
});

/** Gradiente horizontal — usado nas barras deitadas dos rankings. */
export const gradienteHorizontal = (corInicio, corFim) => ({
  linearGradient: { x1: 0, y1: 0, x2: 1, y2: 0 },
  stops: [
    [0, corInicio],
    [1, corFim || corInicio],
  ],
});

/**
 * Sombreado de cilindro: escuro na borda esquerda, claro pouco antes do
 * meio, escuro de novo à direita. É esse gradiente de três paradas —
 * e não a projeção 3D — que dá o volume das colunas nas referências.
 */
export const gradienteCilindro = (corClara, corEscura) => ({
  linearGradient: { x1: 0, y1: 0, x2: 1, y2: 0 },
  stops: [
    [0, corEscura || corClara],
    [0.42, corClara],
    [0.62, corClara],
    [1, corEscura || corClara],
  ],
});

/**
 * Monta o texto de título/subtítulo a partir dos segmentos coloridos do spec.
 *
 * IMPORTANTE: não usar `useHTML: true` em lugar nenhum. Rótulos em HTML
 * viram <foreignObject> e simplesmente não existem no SVG exportado — o
 * preview fica certo e a imagem sai sem o texto. Este subconjunto de
 * markup (<span style="color:…;font-size:…">) o Highcharts converte em
 * <tspan> nativo, que sobrevive à exportação. Verificado no SVG real.
 */
export const montarTextoSegmentado = (segmentos, corPadrao = TEXTO_PADRAO) => {
  if (!Array.isArray(segmentos)) return segmentos || "";
  return segmentos
    .filter((s) => s && s.texto)
    .map((s) => {
      const estilos = [`color:${s.cor || corPadrao}`];
      if (s.tamanho) estilos.push(`font-size:${s.tamanho * ESCALA}px`);
      return `<span style="${estilos.join(";")}">${escaparTexto(s.texto)}</span>`;
    })
    .join("");
};

/** Evita que um título com < ou & quebre o parse do markup do Highcharts. */
export const escaparTexto = (texto) =>
  String(texto ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const paraRgb = (hex) => {
  const h = String(hex).replace("#", "");
  const c = h.length === 3 ? h.split("").map((x) => x + x).join("") : h;
  return [parseInt(c.slice(0, 2), 16), parseInt(c.slice(2, 4), 16), parseInt(c.slice(4, 6), 16)];
};

const paraHex = (rgb) =>
  "#" + rgb.map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0")).join("");

/**
 * Cor em `t` (0..1) ao longo de uma lista de paradas.
 * Usada nas séries longas, onde cada coluna recebe uma cor da rampa em
 * vez de todas terem a mesma — é o que dá o efeito de arco-íris do
 * gráfico de trigo mensal.
 */
export const interpolarCores = (paradas, t) => {
  if (!paradas?.length) return "#FFFFFF";
  if (paradas.length === 1) return paradas[0];
  const pos = Math.max(0, Math.min(1, t)) * (paradas.length - 1);
  const i = Math.min(paradas.length - 2, Math.floor(pos));
  const f = pos - i;
  const a = paraRgb(paradas[i]);
  const b = paraRgb(paradas[i + 1]);
  return paraHex([0, 1, 2].map((k) => a[k] + (b[k] - a[k]) * f));
};

/** Escurece uma cor — usado para a base do gradiente de cada coluna. */
export const escurecer = (hex, fator = 0.45) =>
  paraHex(paraRgb(hex).map((v) => v * (1 - fator)));

/** Formata um número no padrão brasileiro, conforme as opções do spec. */
export const formatarValor = (valor, opcoes = {}) => {
  const { decimais = 1, prefixo = "", sufixo = "" } = opcoes;
  if (valor === null || valor === undefined || Number.isNaN(valor)) return "";
  const texto = Number(valor).toLocaleString("pt-BR", {
    minimumFractionDigits: decimais,
    maximumFractionDigits: decimais,
  });
  return `${prefixo}${texto}${sufixo}`;
};

/**
 * Decide se um ponto recebe rótulo.
 * "extremos" = só o primeiro e o último, padrão das séries longas
 * (ex.: o gráfico de trigo mar-23 a ago-26, que rotula só as pontas).
 */
export const deveRotular = (indice, total, exibirEm = "todos") => {
  if (exibirEm === "nenhum") return false;
  if (exibirEm === "extremos") return indice === 0 || indice === total - 1;
  if (Array.isArray(exibirEm)) return exibirEm.includes(indice);
  return true;
};

/* ------------------------------------------------------------------ *
 * Translucidez, escala de fonte e estilo visual
 * ------------------------------------------------------------------ */

/**
 * Converte um hex em rgba com a opacidade pedida.
 *
 * O JPEG final não tem canal alfa, mas a translucidez DENTRO do desenho
 * funciona normalmente: o que fica por baixo (parede, imagem de fundo,
 * colunas) é composto na hora de rasterizar. É isso que dá o efeito de
 * vidro sem depender de filtro de CSS, que não sobreviveria à exportação.
 */
export const comAlfa = (cor, alfa = 1) => {
  if (!cor || typeof cor !== "string") return cor;
  if (cor.startsWith("rgba") || cor.startsWith("rgb")) return cor;
  const [r, g, b] = paraRgb(cor);
  return `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(1, alfa))})`;
};

/**
 * Área segura: folga mínima entre o conteúdo e a borda da imagem.
 *
 * Os valores estão em pixels da imagem final (604x366), não na escala das
 * fontes — é a medida que o usuário enxerga no painel. Cada família define
 * as próprias margens conforme o que precisa reservar (eixo, categorias,
 * setas); esta folga é somada por cima delas em construirOpcoes, num lugar
 * só, para que nenhuma família nasça encostando na borda.
 */
export const MARGENS_PADRAO = { topo: 6, direita: 8, baixo: 8, esquerda: 8 };

/** Estilo visual padrão dos gráficos novos. */
export const ESTILO_PADRAO = {
  visual: "moderno", // "moderno" | "classico"
  translucidez: 0.55, // opacidade do fundo das caixas de rótulo
  cantos: 5, // raio dos cantos arredondados
  contorno: 0.32, // opacidade da borda das caixas
};

/**
 * Quantos rótulos de valor a imagem vai ter que acomodar.
 *
 * É esse número — e não a quantidade de pontos — que define se o texto
 * cabe: uma série de 40 pontos que só rotula as pontas continua folgada.
 */
export const contarRotulosVisiveis = (spec) => {
  const categorias = spec?.dados?.categorias?.length || 0;
  const series = spec?.dados?.series?.length || 1;
  const modo = spec?.valores?.exibirEm ?? "todos";
  if (modo === "nenhum") return 0;
  if (modo === "extremos") return 2 * series;
  if (Array.isArray(modo)) return modo.length * series;
  return categorias * series;
};

/**
 * Fator aplicado a TODOS os tamanhos de fonte.
 *
 * Combina o ajuste manual do usuário com um automático: até 10 rótulos a
 * fonte fica no tamanho cheio; acima disso encolhe pela raiz da razão (a
 * raiz, e não a razão direta, porque a área disponível cresce nas duas
 * dimensões — encolher linearmente deixaria o texto pequeno demais). O
 * piso de 0,5 evita gerar um gráfico ilegível por acidente.
 */
export const calcularEscalaFonte = (spec) => {
  const cfg = spec?.fontes || {};
  const manual = Number(cfg.escala) > 0 ? Number(cfg.escala) : 1;
  if (cfg.automatico === false) return manual;
  const n = contarRotulosVisiveis(spec);
  const automatico = n <= 10 ? 1 : Math.max(0.5, Math.sqrt(10 / n));
  return Number((manual * automatico).toFixed(3));
};

/** Tamanhos de fonte já com a escala aplicada. */
export const fontesEscaladas = (spec) => {
  const fator = calcularEscalaFonte(spec);
  return Object.fromEntries(
    Object.entries(FONTES).map(([chave, valor]) => [chave, Math.round(valor * fator)])
  );
};

/**
 * Calcula a variação percentual entre dois valores.
 * Devolve null quando não dá para calcular (base zero ou ausente), para
 * que o desenho possa simplesmente omitir o selo em vez de mostrar
 * "Infinity%" ou "NaN%".
 */
export const variacaoPercentual = (de, para) => {
  const a = Number(de);
  const b = Number(para);
  if (!Number.isFinite(a) || !Number.isFinite(b) || a === 0) return null;
  return ((b - a) / Math.abs(a)) * 100;
};

/** Formata uma variação com sinal, no padrão brasileiro. */
export const formatarVariacao = (percentual, decimais = 1) => {
  if (percentual === null || percentual === undefined) return "";
  // O sinal sai do número JÁ arredondado: -0,4% com zero casas vira "0%",
  // e não o "-0%" que aparecia antes.
  const fator = 10 ** decimais;
  const arredondado = Math.round(percentual * fator) / fator;
  const sinal = arredondado > 0 ? "+" : arredondado < 0 ? "-" : "";
  const absoluto = Math.abs(arredondado).toLocaleString("pt-BR", {
    minimumFractionDigits: decimais,
    maximumFractionDigits: decimais,
  });
  return `${sinal}${absoluto}%`;
};
